import { FormLabel, FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { memo, useCallback, useState, useMemo, forwardRef, useImperativeHandle, useRef } from "react";
import { format, parse, isValid } from "date-fns";
import { fr } from "date-fns/locale";
import { type DateSelectorProps, type DateSelectorRef } from "./types";
import { useFormContext } from "react-hook-form";

export const DateSelector = memo(
  forwardRef<DateSelectorRef, DateSelectorProps>(function DateSelector({ startDateValue, endDateValue, onStartDateChange, onEndDateChange }, ref) {
    const [startDateOpen, setStartDateOpen] = useState(false);
    const [endDateOpen, setEndDateOpen] = useState(false);
    const form = useFormContext();
    const containerRef = useRef<HTMLDivElement>(null);

    // Expose scroll function to parent
    useImperativeHandle(
      ref,
      () => ({
        scrollIntoView: () => {
          containerRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "end",
            inline: "nearest",
          });
          // Focus on the first date input after scrolling
          setTimeout(() => {
            const firstInput = containerRef.current?.querySelector("input");
            firstInput?.focus();
          }, 500);
        },
      }),
      []
    );

    // Format date input with automatic "/" insertion
    const formatDateInput = useCallback((value: string) => {
      // Remove all non-digits
      const digits = value.replace(/\D/g, "");

      // Add slashes automatically
      if (digits.length >= 2) {
        if (digits.length >= 4) {
          return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
        }
        return `${digits.slice(0, 2)}/${digits.slice(2)}`;
      }
      return digits;
    }, []);

    // Parse date string to Date object
    const parseDate = useCallback((dateStr: string): Date | undefined => {
      if (!dateStr || dateStr.length < 10) return undefined;

      try {
        const parsed = parse(dateStr, "dd/MM/yyyy", new Date());
        return isValid(parsed) ? parsed : undefined;
      } catch {
        return undefined;
      }
    }, []);

    // Convert Date object to formatted string
    const formatDate = useCallback((date: Date): string => {
      return format(date, "dd/MM/yyyy");
    }, []);

    // Memoized parsed dates
    const startDateParsed = useMemo(() => parseDate(startDateValue), [startDateValue, parseDate]);
    const endDateParsed = useMemo(() => parseDate(endDateValue), [endDateValue, parseDate]);

    const handleStartDateInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatDateInput(e.target.value);
        if (formatted.length <= 10) {
          onStartDateChange(formatted);
        }
      },
      [formatDateInput, onStartDateChange]
    );

    const handleEndDateInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatDateInput(e.target.value);
        if (formatted.length <= 10) {
          onEndDateChange(formatted);
        }
      },
      [formatDateInput, onEndDateChange]
    );

    const handleStartDateSelect = useCallback(
      (date: Date | undefined) => {
        if (date) {
          onStartDateChange(formatDate(date));
          setStartDateOpen(false);
        }
      },
      [formatDate, onStartDateChange]
    );

    const handleEndDateSelect = useCallback(
      (date: Date | undefined) => {
        if (date) {
          onEndDateChange(formatDate(date));
          setEndDateOpen(false);
        }
      },
      [formatDate, onEndDateChange]
    );

    return (
      <div ref={containerRef} className="mb-10 grid grid-cols-1 gap-6 md:grid-cols-2">
        <FormField
          control={form.control}
          name="startDate"
          render={() => (
            <FormItem className="flex flex-col">
              <FormLabel className="text-base font-medium text-gray-900">Date d'ouverture</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    placeholder="jj/mm/aaaa"
                    className="h-12 border-gray-200 pl-8 text-base"
                    value={startDateValue}
                    onChange={handleStartDateInputChange}
                    maxLength={10}
                  />
                  <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="default"
                        className="absolute top-1/2 right-2 h-8 w-8 -translate-y-1/2 p-0 text-gray-400 hover:text-gray-600"
                      >
                        <CalendarIcon className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startDateParsed}
                        onSelect={handleStartDateSelect}
                        locale={fr}
                        disabled={(date) => date < new Date("1900-01-01")}
                        autoFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="endDate"
          render={() => (
            <FormItem className="flex flex-col">
              <FormLabel className="text-base font-medium text-gray-900">Date de fermeture</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    placeholder="jj/mm/aaaa"
                    className="h-12 border-gray-200 pl-8 text-base"
                    value={endDateValue}
                    onChange={handleEndDateInputChange}
                    maxLength={10}
                  />
                  <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="default"
                        className="absolute top-1/2 right-2 h-8 w-8 -translate-y-1/2 p-0 text-gray-400 hover:text-gray-600"
                      >
                        <CalendarIcon className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endDateParsed}
                        onSelect={handleEndDateSelect}
                        locale={fr}
                        disabled={(date) => {
                          // Disable dates before start date if start date is selected
                          if (startDateParsed) {
                            return date < startDateParsed;
                          }
                          return date < new Date("1900-01-01");
                        }}
                        autoFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    );
  })
);
